import React, { useCallback, useEffect, useState } from 'react';
import { FiPlus, FiRefreshCw, FiSearch } from 'react-icons/fi';
import { toast } from 'react-toastify';
import adminService from '../../services/adminService';
import StatCard from './components/StatCard';
import AdTable from './components/AdTable';
import AdFormModal from './components/AdFormModal';
import AdViewModal from './components/AdViewModal';

const AdvertisingManagement = () => {
  const [ads, setAds] = useState([]);
  const [stats, setStats] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, pages: 1 });
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingAd, setEditingAd] = useState(null);
  const [viewingAd, setViewingAd] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPagination((prev) => ({ ...prev, page: 1 }));
    }, 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const fetchAds = useCallback(async () => {
    try {
      setLoading(true);
      const response = await adminService.getDisplayAds({
        page: pagination.page,
        limit: pagination.limit,
        search: debouncedSearch,
        status: statusFilter
      });
      if (response.success) {
        setAds(response.data || []);
        setPagination((prev) => ({ ...prev, ...(response.pagination || {}) }));
      }
    } catch (error) {
      toast.error(error.message || error.error || 'Failed to load advertisements');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, debouncedSearch, statusFilter]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await adminService.getDisplayAdStats();
      if (response.success) {
        setStats(response.data);
      }
    } catch (error) {
      toast.error(error.message || error.error || 'Failed to load advertisement stats');
    }
  }, []);

  useEffect(() => {
    fetchAds();
  }, [fetchAds]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const handleRefresh = () => {
    fetchAds();
    fetchStats();
  };

  const handleCopyEmbed = async (ad) => {
    try {
      await navigator.clipboard.writeText(ad.embedCode);
      toast.success('Embed code copied to clipboard');
    } catch (error) {
      toast.error('Failed to copy embed code');
    }
  };

  const handleCreateOrUpdate = async (formPayload) => {
    if (!editingAd && !formPayload.creativeFile) {
      toast.error('Please upload a creative file');
      return;
    }

    try {
      setSubmitting(true);
      const formData = new FormData();
      formData.append('advertiserName', formPayload.advertiserName);
      formData.append('title', formPayload.title || '');
      formData.append('description', formPayload.description || '');
      formData.append('landingUrl', formPayload.landingUrl);
      formData.append('clickUrl', formPayload.clickUrl || '');
      formData.append('startDate', formPayload.startDate);
      formData.append('endDate', formPayload.endDate);
      formData.append('weight', String(formPayload.weight || 1));
      formData.append('status', formPayload.status || 'draft');
      formData.append('displayMode', formPayload.displayMode || 'popup');
      formData.append('popupDelay', String(formPayload.popupDelay ?? 0));
      formData.append('closeable', String(formPayload.closeable !== false));
      formData.append('closeOnBackdrop', String(formPayload.closeOnBackdrop !== false));
      formData.append('showOncePerSession', String(formPayload.showOncePerSession !== false));
      formData.append('popupMaxWidth', String(formPayload.popupMaxWidth ?? 430));

      if (formPayload.creativeFile) {
        formData.append('creative', formPayload.creativeFile);
      }

      const response = editingAd
        ? await adminService.updateDisplayAd(editingAd._id, formData)
        : await adminService.createDisplayAd(formData);

      if (response.success) {
        toast.success(response.message || 'Advertisement saved');
        setShowForm(false);
        setEditingAd(null);
        fetchAds();
        fetchStats();
      }
    } catch (error) {
      toast.error(error.message || error.error || 'Failed to save advertisement');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (ad) => {
    if (!window.confirm(`Delete advertisement for ${ad.advertiserName}?`)) return;

    try {
      const response = await adminService.deleteDisplayAd(ad._id);
      if (response.success) {
        toast.success('Advertisement deleted');
        fetchAds();
        fetchStats();
      }
    } catch (error) {
      toast.error(error.message || error.error || 'Failed to delete advertisement');
    }
  };

  const handleToggleStatus = async (ad) => {
    try {
      const response = await adminService.toggleDisplayAdStatus(ad._id);
      if (response.success) {
        toast.success(response.message || 'Status updated');
        fetchAds();
        fetchStats();
      }
    } catch (error) {
      toast.error(error.message || error.error || 'Failed to update status');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Advertising Network</h1>
          <p className="mt-0.5 text-sm text-slate-500">
            Create display ads, upload creatives, and distribute them to publisher websites via embed code.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={handleRefresh}
            disabled={loading}
            className="inline-flex items-center rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 disabled:opacity-60"
          >
            <FiRefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            type="button"
            onClick={() => {
              setEditingAd(null);
              setShowForm(true);
            }}
            className="inline-flex items-center rounded-xl bg-orange-500 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-orange-600"
          >
            <FiPlus className="mr-2 h-4 w-4" />
            Create Ad
          </button>
        </div>
      </div>

      {stats && (
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4 xl:grid-cols-7">
          <StatCard label="Total Ads" value={stats.total} />
          <StatCard label="Active" value={stats.active} accent="text-green-600" />
          <StatCard label="Paused" value={stats.paused} accent="text-yellow-600" />
          <StatCard label="Draft" value={stats.draft} />
          <StatCard label="Expired" value={stats.expired} accent="text-red-600" />
          <StatCard label="Impressions" value={stats.totalImpressions} />
          <StatCard label="Clicks" value={stats.totalClicks} />
        </div>
      )}

      <div className="flex flex-col gap-3 rounded-2xl border border-slate-200/80 bg-white p-4 shadow-sm sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <FiSearch className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder="Search by advertiser, title, or unique ID"
            className="w-full rounded-xl border border-slate-200 py-2 pl-10 pr-3 text-sm focus:ring-2 focus:ring-orange-500"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(event) => {
            setStatusFilter(event.target.value);
            setPagination((prev) => ({ ...prev, page: 1 }));
          }}
          className="rounded-xl border border-slate-200 px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500"
        >
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="paused">Paused</option>
          <option value="draft">Draft</option>
          <option value="expired">Expired</option>
        </select>
      </div>

      <AdTable
        ads={ads}
        loading={loading}
        onView={setViewingAd}
        onEdit={(ad) => {
          setEditingAd(ad);
          setShowForm(true);
        }}
        onDelete={handleDelete}
        onToggleStatus={handleToggleStatus}
        onCopyEmbed={handleCopyEmbed}
      />

      {pagination.pages > 1 && (
        <div className="flex items-center justify-between rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm">
          <span className="text-slate-500">
            Page {pagination.page} of {pagination.pages} ({pagination.total} total)
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={pagination.page <= 1}
              onClick={() => setPagination((prev) => ({ ...prev, page: prev.page - 1 }))}
              className="rounded-lg border px-3 py-1.5 disabled:opacity-50"
            >
              Previous
            </button>
            <button
              type="button"
              disabled={pagination.page >= pagination.pages}
              onClick={() => setPagination((prev) => ({ ...prev, page: prev.page + 1 }))}
              className="rounded-lg border px-3 py-1.5 disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      )}

      <AdFormModal
        open={showForm}
        editingAd={editingAd}
        onClose={() => {
          setShowForm(false);
          setEditingAd(null);
        }}
        onSubmit={handleCreateOrUpdate}
        submitting={submitting}
      />

      <AdViewModal
        ad={viewingAd}
        onClose={() => setViewingAd(null)}
        onCopyEmbed={handleCopyEmbed}
      />
    </div>
  );
};

export default AdvertisingManagement;
